import CloseIcon from '@material-design-icons/svg/filled/close.svg?react';
import FilterAltOffIcon from '@material-design-icons/svg/filled/filter_alt_off.svg?react';
import KeyboardArrowDownIcon from '@material-design-icons/svg/filled/keyboard_arrow_down.svg?react';
import SearchIcon from '@material-design-icons/svg/filled/search.svg?react';
import SwapVertIcon from '@material-design-icons/svg/filled/swap_vert.svg?react';
import TuneIcon from '@material-design-icons/svg/filled/tune.svg?react';
import type { SortingState } from '@tanstack/react-table';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useDebounce } from '../../hooks/useDebounce';
import { cn } from '../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

/**
 * Query controls for the admin list pages, in the tickets queue's grammar:
 * a segmented quick filter, search, selects, a sort menu, and a compact mode
 * that folds the selects behind a "Filters" toggle on phones.
 *
 * Native selects and inputs inherit font-size from their wrapper (the
 * unlayered normalize in index.css overrides Tailwind text-* on controls), so
 * containers set the size and these classes only handle shape and colour.
 */

export const toolbarControlClassName =
  'inline-flex h-9 items-center gap-1.5 rounded-lg border border-neutral-800 bg-neutral-900 px-3 font-medium text-neutral-300 transition-colors hover:border-neutral-700 hover:bg-neutral-800 hover:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-teal-500';

export interface ToolbarOption<V extends string = string> {
  value: V;
  label: string;
}

export function SegmentedControl<V extends string>({
  label,
  value,
  options,
  onChange,
  className,
}: {
  label: string;
  value: V;
  options: ReadonlyArray<ToolbarOption<V>>;
  onChange: (value: V) => void;
  className?: string;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className={cn(
        'flex max-w-full shrink-0 gap-0.5 overflow-x-auto rounded-lg bg-neutral-900 p-0.5 ring-1 ring-neutral-800 scrollbar-hide',
        className,
      )}
    >
      {options.map((option) => {
        const isActive = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={isActive}
            onClick={() => onChange(option.value)}
            className={cn(
              'whitespace-nowrap rounded-md px-3 py-1.5 font-medium transition-colors',
              isActive
                ? 'bg-neutral-800 text-white shadow-sm'
                : 'text-neutral-400 hover:text-neutral-200',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export function ToolbarSearch({
  label,
  placeholder,
  value,
  onChange,
}: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const [localValue, setLocalValue] = useState(value);
  const debouncedValue = useDebounce(localValue, 400);
  const lastEmittedRef = useRef(value);

  useEffect(() => {
    setLocalValue(value);
    lastEmittedRef.current = value;
  }, [value]);

  useEffect(() => {
    if (debouncedValue !== lastEmittedRef.current) {
      lastEmittedRef.current = debouncedValue;
      onChange(debouncedValue);
    }
  }, [debouncedValue, onChange]);

  return (
    <div className="relative">
      <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 fill-current text-neutral-500" />
      <input
        type="text"
        aria-label={label}
        placeholder={placeholder}
        value={localValue}
        onChange={(event) => setLocalValue(event.target.value)}
        className="h-9 w-full rounded-lg border border-neutral-800 bg-neutral-900 pl-8 pr-8 text-white placeholder-neutral-500 transition-colors hover:border-neutral-700 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
      />
      {localValue && (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => {
            setLocalValue('');
            lastEmittedRef.current = '';
            onChange('');
          }}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-neutral-500 transition-colors hover:text-white"
        >
          <CloseIcon className="size-3.5 fill-current" />
        </button>
      )}
    </div>
  );
}

export function ToolbarSelect<V extends string>({
  label,
  value,
  options,
  onChange,
  inactiveValue,
  className,
}: {
  label: string;
  value: V;
  options: ReadonlyArray<ToolbarOption<V>>;
  onChange: (value: V) => void;
  /** The "everything" option; any other value tints the control. Default `all`. */
  inactiveValue?: V;
  className?: string;
}) {
  const isActive = value !== (inactiveValue ?? 'all');

  return (
    <div className={cn('relative min-w-0', className)}>
      <select
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.target.value as V)}
        className={cn(
          'h-9 w-full cursor-pointer appearance-none rounded-lg border bg-neutral-900 pl-3 pr-8 transition-colors focus:outline-none focus:ring-1 focus:ring-teal-500',
          isActive
            ? 'border-teal-500/40 text-teal-100'
            : 'border-neutral-800 text-neutral-300 hover:border-neutral-700',
        )}
        style={{ colorScheme: 'dark' }}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <KeyboardArrowDownIcon className="pointer-events-none absolute right-2 top-1/2 size-4 -translate-y-1/2 fill-current text-neutral-500" />
    </div>
  );
}

export interface SortOption {
  id: string;
  desc: boolean;
  label: string;
}

export function sortKey(id: string, desc: boolean) {
  return `${id}:${desc ? 'desc' : 'asc'}`;
}

export function SortMenu({
  label,
  options,
  sorting,
  onSortingChange,
  className,
}: {
  label: string;
  options: ReadonlyArray<SortOption>;
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
  className?: string;
}) {
  const primary = sorting[0];
  const currentKey = primary
    ? sortKey(primary.id, primary.desc)
    : sortKey(options[0].id, options[0].desc);
  const current = options.find(
    (option) => sortKey(option.id, option.desc) === currentKey,
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(toolbarControlClassName, className)}
          aria-label={label}
        >
          <SwapVertIcon className="size-4 fill-current text-neutral-400" />
          <span className="truncate">{current?.label ?? 'Sort'}</span>
          <KeyboardArrowDownIcon className="ml-auto size-4 shrink-0 fill-current text-neutral-500" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={6}
        className="w-52 rounded-lg border border-neutral-700 bg-neutral-900 p-1 text-white"
      >
        <DropdownMenuRadioGroup
          value={currentKey}
          onValueChange={(value) => {
            const [id, direction] = value.split(':');
            onSortingChange([{ id, desc: direction === 'desc' }]);
          }}
        >
          {options.map((option) => (
            <DropdownMenuRadioItem
              key={sortKey(option.id, option.desc)}
              value={sortKey(option.id, option.desc)}
              className="cursor-pointer rounded text-sm text-neutral-200 focus:bg-neutral-800 focus:text-white"
            >
              {option.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function ClearFiltersButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-9 items-center gap-1.5 rounded-lg px-2.5 font-medium text-neutral-400 transition-colors hover:bg-neutral-800 hover:text-white"
    >
      <FilterAltOffIcon className="size-4 fill-current" />
      Clear
    </button>
  );
}

interface ListToolbarProps {
  /** Segmented quick filter, rendered first. */
  segments?: ReactNode;
  search: ReactNode;
  /** Selects; inline on desktop, behind the Filters toggle when compact. */
  filters?: ReactNode;
  /** Sort menu and other controls that follow the selects. */
  trailing?: ReactNode;
  compact: boolean;
  activeFilterCount: number;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
}

export function ListToolbar({
  segments,
  search,
  filters,
  trailing,
  compact,
  activeFilterCount,
  hasActiveFilters,
  onClearFilters,
}: ListToolbarProps) {
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(
    activeFilterCount > 0,
  );
  const clearButton = hasActiveFilters ? (
    <ClearFiltersButton onClick={onClearFilters} />
  ) : null;

  return (
    <div className="space-y-2 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        {segments}
        <div className="min-w-[180px] flex-1 text-base sm:text-sm">
          {search}
        </div>
        {compact ? (
          <button
            type="button"
            aria-expanded={isFilterPanelOpen}
            onClick={() => setIsFilterPanelOpen((open) => !open)}
            className={cn(
              toolbarControlClassName,
              isFilterPanelOpen &&
                'border-neutral-700 bg-neutral-800 text-white',
            )}
          >
            <TuneIcon className="size-4 fill-current text-neutral-400" />
            Filters
            {activeFilterCount > 0 && (
              <span className="rounded-full bg-teal-500/20 px-1.5 text-[11px] leading-4 text-teal-200">
                {activeFilterCount}
              </span>
            )}
          </button>
        ) : (
          <>
            {filters}
            {trailing}
            {clearButton}
          </>
        )}
      </div>

      {compact && isFilterPanelOpen && (
        <div className="grid grid-cols-2 gap-2 rounded-xl border border-neutral-800 bg-neutral-900/60 p-2 text-base sm:text-sm">
          {filters}
          {trailing && (
            <div className="col-span-2 flex flex-wrap gap-2">{trailing}</div>
          )}
          {clearButton && <div className="col-span-2 flex">{clearButton}</div>}
        </div>
      )}
    </div>
  );
}
